import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Statistic, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface Row {
  department_id: string
  department_name: string
  total_machines: number
  breakdown_count: number
  critical_breakdown_count: number
  total_downtime_minutes: number
  average_downtime_minutes: number
  pm_compliance_pct: number
  repeat_breakdown_count: number
  maintenance_cost: number
  top_failure_reason_id: string | null
  top_problem_machine_id: string | null
  // Phase 3.1 — backend-calculated, never edited in UI
  production_kg: number | null
  cost_per_kg: number | null
  cost_per_kg_match_level: string
}

/**
 * Spec §5 — Department-wise breakdown report.
 */
export default function DepartmentBreakdownReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { failureReasonName, machineCode } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-dept-breakdown', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.departmentBreakdown(filters) as unknown as Promise<Row[]>,
  })
  const rows = data ?? []
  const totalBd = rows.reduce((a, r) => a + (r.breakdown_count ?? 0), 0)
  const totalDt = rows.reduce((a, r) => a + (r.total_downtime_minutes ?? 0), 0)
  const totalCost = rows.reduce((a, r) => a + (r.maintenance_cost ?? 0), 0)

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/department-breakdown', filters,
        `department-breakdown-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    { title: 'Department', dataIndex: 'department_name', key: 'dn' },
    { title: 'Machines', dataIndex: 'total_machines', key: 'tm', width: 100 },
    { title: 'Breakdowns', dataIndex: 'breakdown_count', key: 'bk', width: 110 },
    { title: 'Critical', dataIndex: 'critical_breakdown_count', key: 'cb', width: 100,
      render: (v: number) => v > 0 ? <Text type="danger" strong>{v}</Text> : v,
    },
    { title: 'Downtime (min)', dataIndex: 'total_downtime_minutes', key: 'dt', width: 140 },
    { title: 'Avg DT (min)', dataIndex: 'average_downtime_minutes', key: 'ad', width: 110 },
    { title: 'PM Compliance', dataIndex: 'pm_compliance_pct', key: 'pmc', width: 130,
      render: (v: number) => (
        <Text style={{ color: v < 80 ? '#cf1322' : '#52c41a' }}>{v.toFixed(1)}%</Text>
      ),
    },
    { title: 'Repeat BD', dataIndex: 'repeat_breakdown_count', key: 'rb', width: 110 },
    { title: 'Cost', dataIndex: 'maintenance_cost', key: 'cost', width: 120,
      render: (v: number) => `₹ ${v.toFixed(2)}` },
    // Phase 3.1 — production kg + cost/kg (backend-calculated)
    { title: 'Production kg', dataIndex: 'production_kg', key: 'pk', width: 130,
      render: (v: number | null) => v == null ? <Text type="secondary">—</Text> : v.toFixed(2),
    },
    {
      title: 'Cost / kg (₹)', dataIndex: 'cost_per_kg', key: 'cpk', width: 160,
      render: (v: number | null, r: Row) => v == null
        ? <Text type="secondary">—</Text>
        : <Text strong>₹ {v.toFixed(4)} <Text type="secondary">({r.cost_per_kg_match_level})</Text></Text>,
    },
    {
      title: 'Top failure reason', key: 'tfr', width: 200,
      render: (_: unknown, r: Row) => failureReasonName(r.top_failure_reason_id),
    },
    {
      title: 'Top problem machine', key: 'tpm', width: 200,
      render: (_: unknown, r: Row) => machineCode(r.top_problem_machine_id),
    },
  ]

  return (
    <div>
      <PageHeader title="Department-wise breakdown"
        subtitle="Which department is causing the most maintenance loss?" />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Departments" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Total breakdowns" value={totalBd} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Downtime (min)" value={totalDt} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Cost" value={totalCost} prefix="₹" precision={2} /></Card></Col>
      </Row>
      <DataTable rowKey="department_id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
